import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TreeItem } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import CustomTextField from "./CustomTextField.tsx";
import {DefaultButton} from "./Buttons.tsx";
import CustomCheckbox from "./CustomCheckbox.tsx";

interface customCategoryTreeProps {
  singleSelection?:boolean;
  isMultiSelect?:boolean;
  selectedItems?: Map<number, string>;
  onSelectedItemsChange?: (selectedItems) => void;
  disabledItems?: Set<number>;
  preloadedCategories?: any[]; // IF you pass a list -> it uses preloadedCategories.
  // If you pass null -> It loads them from the backend
}

// Tree item: id, path, name
const CategoryTree : React.FC<customCategoryTreeProps> = (
  {
    singleSelection= false,
    isMultiSelect=false,
    selectedItems,
    onSelectedItemsChange,
    disabledItems = new Set(),
    preloadedCategories,
    ...props
  }) => {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [allPaths, setAllPaths] = useState<string[] | null>(null);

  useEffect(() => {
    if (preloadedCategories) {
      const treeData = buildTree(preloadedCategories);
      setCategories(treeData);
      setAllPaths(getAllPaths(treeData));
    }
    else {
      const fetchData = async () => {
        const config = {headers: {"Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}`,},};
        await axios.get(`${backendUrl}/general/get_categories/`, config)
          .then(response => {
            const treeData = buildTree(response.data);
            setCategories(treeData);
            setAllPaths(getAllPaths(treeData));
            // setFilteredCategories(treeData);
          })
          .catch(error => alert("Fehler beim Laden der Kategorien " + error.toString()))
      };
      fetchData();
    }

  }, [preloadedCategories]);

  // Gets a flat list of categories and builds the tree-structure that can be used by the TreeView
  const buildTree = (categories) => {
    const map = {};
    categories.forEach(cat => map[cat.path] = { ...cat, children: [] }); // Store each category in cat.path
    // use ...cat to also store id, name and path
    const tree = [];
    // Add categories to the tree
    categories.forEach(cat => {
      const parentPath = cat.path.split('.').slice(0, -1).join('.'); // Get parent path
      if (parentPath && map[parentPath]) { // parent path exists and is in the map (indicating it is a child of another category)
        map[parentPath].children.push(map[cat.path]);
      } else { // parent path does not exist (Because cat is the highest possible category (e.g. Wohnzimmer))
        tree.push(map[cat.path]);
      }
    });
    return tree;
  };

  // Method for filtering items from the tree with a search box
  const filterTree = (nodes, term) => {
    return nodes // Return filtered categories with the help of map
      .map((node) => {
        // Ensure children exists
        const children = node.children || [];

        // Check if the current node matches
        const isMatch = node.name.toLowerCase().includes(term.toLowerCase());

        // If this node matches, return it with ALL of its children.
        // This ensures the following behaviour: If I type "Wohnzimmer" I want to get all subcategories of "Wohnzimmer"
        if (isMatch) {
          return { ...node, children};
        }
        // Recursively filter children
        const filteredChildren = filterTree(children, term);

        // If node matches or any of its children match, keep it
        if (isMatch || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }

        return null
      })
      .filter((node) => node !== null);
  };
  // Hook to search if the searchTerm or categories are changed
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCategories(categories);
      setExpanded([]);
    } else {
      const f = filterTree(categories, searchTerm);
      setFilteredCategories(f);
      setExpanded(getAllPaths(f)); // Expand all found items
    }
  }, [searchTerm, categories]);

  // const removeNodeAndDescendants = (node: any, newSelectedItems: Map<number, string>) => {
  //   newSelectedItems.delete(node.id);
  //   node.children.forEach(child => removeNodeAndDescendants(child, newSelectedItems));
  // };
  // // If none of a parent's children remain checked, the parent should also be unchecked
  // const updateParentSelection = (childPath: string, newSelectedItems: Map<number, string>) => {
  //   const parentPath = childPath.split('.').slice(0, -1).join('.');
  //   if (!parentPath) return;
  //   const parentNode = findNodeByPath(parentPath, categories);
  //   if (parentNode) {
  //     const anyChildSelected = parentNode.children.some(child => newSelectedItems.has(child.id));
  //     if (!anyChildSelected) {
  //       newSelectedItems.delete(parentNode.id);
  //       updateParentSelection(parentNode.path, newSelectedItems);
  //     }
  //   }
  // };

  function uncheckNodeAndDescendants(node: any, newSelectedItems: Map<number, string>) {
    // Remove the node itself
    newSelectedItems.delete(node.id);
    // Recursively remove all children
    node.children.forEach((child: any) => {
      uncheckNodeAndDescendants(child, newSelectedItems);
    });
  }

  function uncheckEmptyAncestors(childPath: string, newSelectedItems: Map<number, string>) {
    let currentPath = childPath;

    while (true) {
      // Get the parent path
      const parentPath = currentPath.split('.').slice(0, -1).join('.');
      // Stop if no parent
      if (!parentPath || parentPath === currentPath) break;

      // Find the parent node
      const parentNode = findNodeByPath(parentPath, categories);
      if (!parentNode) break; // Can't go further if not found

      // Is the parent currently selected?
      if (newSelectedItems.has(parentNode.id)) {
        // Check if ALL of its children are unchecked
        const allChildrenUnchecked = parentNode.children.every(
          (child: any) => !newSelectedItems.has(child.id)
        );
        // If yes, uncheck the parent
        if (allChildrenUnchecked) {
          newSelectedItems.delete(parentNode.id);
          // Keep going up in case the parent's parent now has no checked children
          currentPath = parentPath;
          continue;
        }
      }

      // If the parent either isn't selected or still has other checked children, stop
      break;
    }
  }

  // Method to get all the nodes' paths in a list. This is for setting the expanded state of all of them
  const getAllPaths = (nodes) => {
      return nodes.flatMap( node => [
        node.path, ...(node.children ? getAllPaths(node.children) : [])
      ]);
    };
  const findNodeByPath = (path: string, nodes: any[]): any | null => {
      for (const node of nodes) {
        if (node.path === path) return node;
        if (node.children.length > 0) {
          const childNode = findNodeByPath(path, node.children);
          if (childNode) return childNode;
        }
      }
      return null;
    };

  const addParents = (path: string, id: number, newSelectedItems: Map<number, string>) => {
    let currentPath = path;
    let currentId = id;
    while (currentPath) {
      // If this ancestor isn't selected, select it
      if (!newSelectedItems.has(currentId)) {
        newSelectedItems.set(currentId, currentPath);
      }
      const parentPath = currentPath.split('.').slice(0, -1).join('.'); // Get parent path
      if (parentPath === currentPath) break; // Stop if we've reached the root
      const parentNode = findNodeByPath(parentPath, categories); // Search parent in the tree
      // if (parentNode) currentId = parentNode.id;
      // currentPath = parentPath;
      if (!parentNode) break;
      currentId = parentNode.id;
      currentPath = parentNode.path;
    }
  };

  const handleCheckboxChange = (path: string, id: number, isChecked: boolean) => {
    // Prevent changes if the item is disabled
    if (disabledItems.has(id)) return;

    // If not multi-select, start with an empty Map so only one item is selected at a time.
    const newSelectedItems = isMultiSelect ? new Map(selectedItems) : new Map();

    if (isChecked) {
      // Check this node
      newSelectedItems.set(id, path);
      // Also check all parents
      addParents(path, id, newSelectedItems);
    } else {
      // Uncheck this node + all its descendants
      const node = findNodeByPath(path, categories);
      if (node) {
        uncheckNodeAndDescendants(node, newSelectedItems);
        // Now bubble up to see if the ancestors should be unselected
        uncheckEmptyAncestors(path, newSelectedItems);
      }
    }
    onSelectedItemsChange(newSelectedItems);
  };

  const handleSelectionChange = (path: string, id: number) => {
    const newSelectedItems = singleSelection ? new Map() : new Map(selectedItems);

    if (singleSelection) {
      // Single selection: replace the entire selection with the new one
      newSelectedItems.set(id, path);
    } else {
      // Multi-selection: toggle selection
      if (newSelectedItems.has(id)) {
        newSelectedItems.delete(id);
      } else {
        newSelectedItems.set(id, path);
      }
    }

    onSelectedItemsChange?.(newSelectedItems);
  };

  const handleExpandClick = () => {
    if (expanded.length === 0) {
      setExpanded(allPaths);
    }
    else {
      setExpanded([]);
    }
  };


  const renderTree = (nodes) => (
    nodes.map(node => (
      <TreeItem key={node.id}
                itemId={node.path}
                // label={node.name}
                label={
                  <div
                    style={{ display: 'flex', alignItems: 'center' }}
                    // Only select if singleSelection is set to true
                    onClick={() => {
                      if (singleSelection) {handleSelectionChange(node.path, node.id);}
                      // Expand the clicked node if the item is not selected. If it is unselected, remove it from the expanded items
                      if (!selectedItems.has(node.id)) {
                        setExpanded(prevExpanded => {
                          const set = new Set(prevExpanded);
                          set.add(node.path);
                          return Array.from(set);
                        });
                      }
                      else {
                        setExpanded(prevExpanded => prevExpanded.filter(item => item != node.path));
                      }

                    }}
                  >
                    {!singleSelection && (<CustomCheckbox
                      label={node.name}
                      value={selectedItems.has(node.id)}
                      onChange={(checked) => handleCheckboxChange(node.path, node.id, checked)}
                      is_disabled={disabledItems.has(node.id)}
                    />)}
                    {singleSelection && <span>{node.name}</span>}
                  </div>
                }
                // onClick={() => handleCheckboxChange(node.path, node.id, !selectedItems.has(node.id))}
      >
        {node.children.length > 0 && renderTree(node.children)}
      </TreeItem>
    ))
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignContent: 'start', justifyContent: 'center' }}>
      <div style={{display:'flex', flexDirection: 'row', alignItems: 'center', columnGap: '15px', justifyContent: 'center', width: "100%", minWidth: "100%", marginBottom: "15px"}}>
        <CustomTextField label="Suchbegriff"
                       placeholder="Suchbegriff"
                       value={searchTerm}
                       onChange={(e) => setSearchTerm(e.target.value)}
                       addEraseButton={true}
                       sx = {{ width: "60%", minWidth: "60%", height: "100%"}}
        />
        <DefaultButton label={expanded.length === 0 ? "Alle ausklappen" : "Alle einklappen"}
                       onClick={handleExpandClick}
                       sx = {{ width: "175px", minWidth: "35%"}}
        />
      </div>
      <SimpleTreeView
        expandedItems={expanded}
        onExpandedItemsChange={(_, items) => setExpanded(items)}
        checkboxSelection={false}
        multiSelect={isMultiSelect}
        selectedItems={Array.from(selectedItems.values())}
        // onSelectedItemsChange={(_, selectedPaths) => {
        //   const newSelectedItems = new Map(selectedItems);
        //   onSelectedItemsChange(newSelectedItems)
        // }}
        disableSelection={true}
        expansionTrigger="iconContainer"
        sx = {{ width: "100%",
                '& .MuiTreeItem-content': {
                  minHeight: '30px',
                  maxHeight: '30px',
                  padding: '15px 15px',    // reduce left/right padding
                },
                // 3) Adjust the icon container (expand/collapse arrow)
                '& .MuiTreeItem-iconContainer': {
                  width: '15px',       // smaller width
                  marginRight: '2px',  // less spacing before label
                },
                "& .MuiTreeItem-label": {
                  textAlign: "left", // Align text to the left
                  justifyContent: "flex-start", // Ensure content aligns left
                },
                "& .MuiTreeItem-root": {
                  color: "var(--font-primary)",
                },
                "& .MuiTreeItem-content:hover": {
                  backgroundColor: "var(--naturGreenAlpha05) !important", // Light green hover
                },
                "& .MuiTreeItem-content.Mui-selected": {
                  backgroundColor: "var(--naturGreenAlpha05) !important",
                  color: "white",
                },
                "& .MuiTreeItem-content.Mui-selected:hover": {
                  backgroundColor: "var(--naturGreenAlpha05) !important",
                },
                "& .MuiCheckbox-root svg": {
                  color: "var(--font-primary)", // Default color (when not selected)
                },
                "& .MuiCheckbox-root.Mui-checked svg": {
                  color: "var(--naturGreen) !important", // Color when selected
                },
        }}
        {...props}
      >
        {renderTree(filteredCategories)}
      </SimpleTreeView>
    </div>
  );
};

export default CategoryTree;
